import { useState, useEffect, useRef } from 'react';
import { fetchPublicDonors, type PublicDonorItem } from '../../../api/dripPlan';

export default function DonorList() {
  const [donorList, setDonorList] = useState<PublicDonorItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      setLoading(true);
      fetchPublicDonors(200)
        .then((list) => {
          if (!cancelled) setDonorList(list);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    };
    load();
    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || isPaused || donorList.length === 0) return;

    let animationId: number;
    let scrollPosition = 0;

    const scroll = () => {
      scrollPosition += 0.5;
      if (scrollPosition >= scrollContainer.scrollHeight / 2) {
        scrollPosition = 0;
      }
      scrollContainer.scrollTop = scrollPosition;
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, [isPaused, donorList.length]);

  const duplicatedList = donorList.length > 0 ? [...donorList, ...donorList] : [];

  return (
    <div className="bg-white rounded-xl shadow-sm p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-[#1E1E1E]">爱心捐赠名录</h2>
        <div className="flex items-center gap-2 text-sm text-[#6C6C6C]">
          <i className="ri-time-line text-[#2A7A62]"></i>
          <span>实时更新</span>
        </div>
      </div>

      {loading ? (
        <div className="h-[500px] flex items-center justify-center text-[#6C6C6C]">加载中...</div>
      ) : donorList.length === 0 ? (
        <div className="h-[500px] flex items-center justify-center text-[#6C6C6C]">暂无捐赠记录，期待您的爱心参与</div>
      ) : (
      <div
        ref={scrollRef}
        className="h-[500px] overflow-hidden"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
      >
        <div className="space-y-3">
          {duplicatedList.map((donor, index) => (
            <div
              key={`${donor.id}-${index}`}
              className="flex items-center justify-between p-4 bg-[#F9F9F9] rounded-lg hover:bg-[#2A7A62]/5 transition-all"
            >
              <div className="flex items-center gap-4 flex-1">
                <div className="w-12 h-12 bg-gradient-to-br from-[#2A7A62] to-[#2A7A62]/80 rounded-full flex items-center justify-center flex-shrink-0">
                  {donor.isAnonymous ? (
                    <i className="ri-user-line text-xl text-white"></i>
                  ) : (
                    <span className="text-lg font-bold text-white">
                      {donor.name.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-base font-bold text-[#1E1E1E]">
                      {donor.name}
                    </p>
                    {donor.isAnonymous && (
                      <span className="px-2 py-0.5 bg-[#F4B942]/20 text-[#F4B942] text-xs rounded whitespace-nowrap">
                        匿名
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[#6C6C6C]">{donor.date}</p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-xl font-bold text-[#2A7A62]">
                  ¥{donor.amount}
                </p>
                <p className="text-xs text-[#6C6C6C] mt-1">
                  {Math.floor(donor.amount / 19.9) * 12}瓶水
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      <div className="mt-6 text-center">
        <p className="text-sm text-[#6C6C6C] mb-4">
          感谢每一位爱心人士的支持，您的善举温暖了城市的守护者
        </p>
        <div className="flex items-center justify-center gap-8 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-[#2A7A62] rounded-full"></div>
            <span className="text-[#6C6C6C]">实名捐赠</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-[#F4B942] rounded-full"></div>
            <span className="text-[#6C6C6C]">匿名捐赠</span>
          </div>
        </div>
      </div>
    </div>
  );
}
